import React, { useEffect, useMemo, useState } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.petro-marine.net').replace(/\/$/, '');

interface PublicJob {
  id: string;
  title: string;
  department: string;
  location: string;
  employmentType: string;
  experienceLevel: string;
  description: string;
  requirements?: string[] | string;
  responsibilities?: string[] | string;
  salaryRange?: any;
  benefits?: string[] | string;
  contractDuration?: string;
  rotationPeriod?: string;
  isUrgent?: boolean;
  /** Form-based posting: Apply opens the full seafarer form (→ Candidates) */
  useSeafarerForm?: boolean;
  applicationDeadline?: string;
  postedAt?: string;
}

const EXPERIENCE_LABELS: Record<string, string> = {
  entry_level: 'Entry level',
  mid_level: 'Mid level',
  senior: 'Senior',
  executive: 'Executive',
};

const EMPLOYMENT_LABELS: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  temporary: 'Temporary',
};

/** requirements/responsibilities are JSON columns — the ERP stores a JSON-encoded
 * string, older rows may already be arrays. Always return a string array. */
const toStringArray = (value: any): string[] => {
  if (!value) return [];
  let v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return v ? [v] : []; }
  }
  if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean);
  return [String(v)];
};

const decodeSalary = (value: any): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    try {
      const p = JSON.parse(value);
      return typeof p === 'string' ? p : String(p);
    } catch {
      return value;
    }
  }
  return String(value);
};

const CareersPage: React.FC = () => {
  const [showCareerForm, setShowCareerForm] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showJobApplicationModal, setShowJobApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<{title: string, category: string, location: string, experience: string, description?: string, responsibilities?: string[], requirements?: string[], type?: string, useSeafarerForm?: boolean} | null>(null);
  const [jobs, setJobs] = useState<PublicJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submittedRef, setSubmittedRef] = useState('');
  const [seafarerSubmitting, setSeafarerSubmitting] = useState(false);
  const [seafarerError, setSeafarerError] = useState('');
  const [seafarerSubmitted, setSeafarerSubmitted] = useState(false);
  const [jobApplicationData, setJobApplicationData] = useState({
    name: '',
    email: '',
    nationality: '',
    phone: '',
    address: '',
    birthDate: '',
    experienceYears: '',
    expectedSalary: '',
    cvFile: null as File | null
  });
  const [formData, setFormData] = useState({
    email: '',
    // Personal Information
    name: '',
    nationality: '',
    gender: '',
    maritalStatus: '',
    dateOfBirth: '',
    mobile1: '',
    mobile2: '',
    relativeMobile: '',
    availableFrom: '',
    homeAddress: '',
    appliedToRank: '',
    
    // Seaman Book Information
    seamanBookNo: '',
    profession: '',
    dateOfIssue: '',
    expiryDate: '',
    
    // Certificates
    securityAwareness: { available: false, expiry: '' },
    firstAid: { available: false, expiry: '' },
    fireFighting: { available: false, expiry: '' },
    personalSurvival: { available: false, expiry: '' },
    personalSafety: { available: false, expiry: '' },
    medicalCertificate: { available: false, expiry: '' },
    cocOfficers: { available: false, expiry: '' },
    gocDeck: { available: false, expiry: '' },
    deckEngineWatch: { available: false, expiry: '' },
    shipSecurity: { available: false, expiry: '' },
    foodHandler: { available: false, expiry: '' },
    
    // Sea Service Experience (12 rows by default)
    seaService: Array(12).fill(null).map(() => ({
      companyName: '',
      vesselName: '',
      vesselType: '',
      rank: '',
      from: '',
      to: '',
      experienceYears: 0
    }))
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCertificateChange = (cert: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [cert]: {
        ...prev[cert as keyof typeof prev] as any,
        [field]: value
      }
    }));
  };

  const calculateExperienceYears = (from: string, to: string): number => {
    if (!from || !to) return 0;
    
    const fromDate = new Date(from);
    const toDate = new Date(to);
    
    if (toDate <= fromDate) return 0;
    
    // Calculate years and months more precisely
    let years = toDate.getFullYear() - fromDate.getFullYear();
    let months = toDate.getMonth() - fromDate.getMonth();
    let days = toDate.getDate() - fromDate.getDate();
    
    // Adjust for negative days
    if (days < 0) {
      months--;
      const prevMonth = new Date(toDate.getFullYear(), toDate.getMonth(), 0);
      days += prevMonth.getDate();
    }
    
    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }
    
    // Business rule: each month counts as 0.1 years in the displayed total.
    const totalYears = years + (months * 0.1);
    
    // Round to 1 decimal place
    return Math.round(totalYears * 10) / 10;
  };

  const handleSeaServiceChange = (index: number, field: string, value: string) => {
    setFormData(prev => {
      const newSeaService = prev.seaService.map((service, i) => {
        if (i === index) {
          const updatedService = { ...service, [field]: value };
          
          // Automatically calculate experience years when from or to date changes
          if (field === 'from' || field === 'to') {
            const fromDate = field === 'from' ? value : service.from;
            const toDate = field === 'to' ? value : service.to;
            updatedService.experienceYears = calculateExperienceYears(fromDate, toDate);
          }
          
          return updatedService;
        }
        return service;
      });

      return {
        ...prev,
        seaService: newSeaService
      };
    });
  };

  const addSeaServiceRow = () => {
    setFormData(prev => ({
      ...prev,
      seaService: [...prev.seaService, {
        companyName: '',
        vesselName: '',
        vesselType: '',
        rank: '',
        from: '',
        to: '',
        experienceYears: 0
      }]
    }));
  };

  const removeSeaServiceRow = (index: number) => {
    if (formData.seaService.length > 1) {
      setFormData(prev => ({
        ...prev,
        seaService: prev.seaService.filter((_, i) => i !== index)
      }));
    }
  };

  const handleCvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCvFile(file);
    }
  };

  const handleJobApply = (job: PublicJob, category: string) => {
    setSelectedJob({
      title: job.title,
      category,
      location: job.location,
      experience: EXPERIENCE_LABELS[job.experienceLevel] || job.experienceLevel || '',
      description: job.description,
      responsibilities: toStringArray(job.responsibilities),
      requirements: toStringArray(job.requirements),
      type: 'details',
      useSeafarerForm: !!job.useSeafarerForm,
    });
    setShowJobDetails(true);
    setShowCareerForm(false);
  };

  const handleApplyFromDetails = () => {
    // Form-based postings route to the full seafarer application form —
    // those submissions are stored under Candidates, not job applications.
    if (selectedJob?.useSeafarerForm) {
      setShowJobDetails(false);
      setShowCareerForm(true);
      setSeafarerSubmitted(false);
      setSeafarerError('');
      setFormData(prev => ({ ...prev, appliedToRank: selectedJob.title }));
      setTimeout(() => {
        document.getElementById('career-application-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
      return;
    }
    // Open the job application modal
    setShowJobApplicationModal(true);
    setSubmitError('');
    setSubmittedRef('');
    setJobApplicationData({
      name: '',
      email: '',
      nationality: '',
      phone: '',
      address: '',
      birthDate: '',
      experienceYears: '',
      expectedSalary: '',
      cvFile: null
    });
  };

  const handleJobApplicationInputChange = (field: string, value: string) => {
    setJobApplicationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleJobCvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setJobApplicationData(prev => ({
        ...prev,
        cvFile: file
      }));
    }
  };

  const handleJobApplicationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const parts = jobApplicationData.name.trim().split(/\s+/);
      const fd = new FormData();
      fd.append('firstName', parts[0] || '');
      fd.append('lastName', parts.slice(1).join(' '));
      fd.append('email', jobApplicationData.email);
      fd.append('phone', jobApplicationData.phone);
      fd.append('nationality', jobApplicationData.nationality);
      fd.append('dateOfBirth', jobApplicationData.birthDate);
      fd.append('appliedPosition', selectedJob?.title || '');
      fd.append('source', 'website');
      fd.append('experience', jobApplicationData.experienceYears);
      fd.append('expectedSalary', jobApplicationData.expectedSalary);
      fd.append('coverLetter', `Address: ${jobApplicationData.address}`);
      if (jobApplicationData.cvFile) fd.append('cv', jobApplicationData.cvFile);

      const res = await fetch(`${API_BASE}/api/website/applications`, {
        method: 'POST',
        body: fd,
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(payload?.error?.message || 'Submission failed — please try again.');
      }
      setSubmittedRef(payload?.data?.id || '');
    } catch (err: any) {
      setSubmitError(err?.message || 'Submission failed — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowJobApplicationModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (seafarerSubmitting) return;
    setSeafarerSubmitting(true);
    setSeafarerError('');
    setSeafarerSubmitted(false);
    try {
      const parts = formData.name.trim().split(/\s+/);
      const totalExperience = formData.seaService
        .reduce((sum, s) => sum + (Number(s.experienceYears) || 0), 0);

      const certLabels: Record<string, string> = {
        securityAwareness: 'Security Awareness',
        firstAid: 'First Aid / Medical Studies',
        fireFighting: 'Fire Fighting',
        personalSurvival: 'Personal Sea Survival Techniques',
        personalSafety: 'Personal Safety and Social Responsibilities',
        medicalCertificate: 'International Medical Certificate for Seafarers',
        cocOfficers: 'COC (Officers)',
        gocDeck: 'GOC (Deck Officers)',
        deckEngineWatch: 'Deck/Engine Watch Keeping',
        shipSecurity: 'Ship Security Officer & Rescue Boats',
        foodHandler: 'Food Handler',
      };
      const heldCertificates = Object.entries(certLabels)
        .filter(([key]) => (formData as any)[key]?.available)
        .map(([key, label]) => {
          const expiry = (formData as any)[key]?.expiry;
          return expiry ? `${label} (expires ${expiry})` : label;
        });

      // Structured copy of the whole seafarer form — the ERP renders this
      // back in the same layout instead of the flat cover-letter text.
      const servedRows = formData.seaService.filter(s => s.companyName || s.vesselName || s.rank);
      const seafarerData = {
        gender: formData.gender || '',
        maritalStatus: formData.maritalStatus || '',
        mobile2: formData.mobile2 || '',
        relativeMobile: formData.relativeMobile || '',
        homeAddress: formData.homeAddress || '',
        marinePassport: {
          number: formData.seamanBookNo || '',
          profession: formData.profession || '',
          issueDate: formData.dateOfIssue || '',
          expiryDate: formData.expiryDate || '',
        },
        certificates: Object.entries(certLabels)
          .filter(([key]) => (formData as any)[key]?.available)
          .map(([key, label]) => ({ name: label, expiry: (formData as any)[key]?.expiry || '' })),
        seaService: servedRows.map(s => ({
          companyName: s.companyName || '',
          vesselName: s.vesselName || '',
          vesselType: s.vesselType || '',
          rank: s.rank || '',
          from: s.from || '',
          to: s.to || '',
          experienceYears: Number(s.experienceYears) || 0,
        })),
      };

      const details: string[] = [];
      if (formData.gender || formData.maritalStatus) {
        details.push(`Gender: ${formData.gender || '-'} | Marital status: ${formData.maritalStatus || '-'}`);
      }
      if (formData.mobile2 || formData.relativeMobile) {
        details.push(`Mobile 2: ${formData.mobile2 || '-'} | Relative: ${formData.relativeMobile || '-'}`);
      }
      if (formData.homeAddress) details.push(`Address: ${formData.homeAddress}`);
      if (formData.seamanBookNo || formData.profession) {
        details.push(
          `Marine passport: ${formData.seamanBookNo || '-'} | Profession: ${formData.profession || '-'}` +
          ` | Issued: ${formData.dateOfIssue || '-'} | Expires: ${formData.expiryDate || '-'}`
        );
      }
      if (servedRows.length) {
        details.push('Sea service:');
        servedRows.forEach(s =>
          details.push(`- ${s.companyName || '-'} / ${s.vesselName || '-'} (${s.vesselType || '-'}) — ${s.rank || '-'}, ${s.from || '?'} → ${s.to || '?'} (${s.experienceYears || 0} yrs)`)
        );
      }

      const lastServed = servedRows[servedRows.length - 1];

      const fd = new FormData();
      fd.append('firstName', parts[0] || '');
      fd.append('lastName', parts.slice(1).join(' '));
      fd.append('email', formData.email);
      fd.append('phone', formData.mobile1);
      fd.append('nationality', formData.nationality);
      fd.append('dateOfBirth', formData.dateOfBirth);
      fd.append('appliedPosition', formData.appliedToRank || 'Seafarer Application');
      fd.append('source', 'website_form');
      fd.append('experience', String(Math.max(0, Math.round(totalExperience))));
      if (formData.availableFrom) fd.append('availableFrom', formData.availableFrom);
      if (lastServed?.companyName) fd.append('previousCompany', lastServed.companyName);
      if (details.length) fd.append('coverLetter', details.join('\n'));
      if (heldCertificates.length) fd.append('certificates', JSON.stringify(heldCertificates));
      fd.append('seafarerData', JSON.stringify(seafarerData));
      if (cvFile) fd.append('cv', cvFile);

      const res = await fetch(`${API_BASE}/api/website/applications`, {
        method: 'POST',
        body: fd,
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(payload?.error?.message || 'Submission failed — please try again.');
      }
      setSeafarerSubmitted(true);
      window.scrollTo({ top: document.getElementById('career-application-form')?.offsetTop || 0, behavior: 'smooth' });
    } catch (err: any) {
      setSeafarerError(err?.message || 'Submission failed — please try again.');
    } finally {
      setSeafarerSubmitting(false);
    }
  };

  // Load live job postings created in the ERP (public endpoint).
  useEffect(() => {
    let cancelled = false;
    setJobsLoading(true);
    fetch(`${API_BASE}/api/website/jobs`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((payload) => {
        if (!cancelled) setJobs(payload?.data || []);
      })
      .catch(() => {
        if (!cancelled) setJobsError('Could not load current openings — please try again later.');
      })
      .finally(() => {
        if (!cancelled) setJobsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Group live postings by department so the existing category layout is kept.
  const jobCategories = useMemo(() => {
    const grouped = new Map<string, PublicJob[]>();
    jobs.forEach((job) => {
      const dept = job.department || 'General';
      grouped.set(dept, [...(grouped.get(dept) || []), job]);
    });
    return Array.from(grouped.entries()).map(([category, categoryJobs]) => ({ category, jobs: categoryJobs }));
  }, [jobs]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-ocean-100">
      <div className="section">
        <h1 className="section-header">Careers & Opportunities</h1>
        
        {/* Job Listings Section */}
        <div className="maritime-card p-8 mb-12">
          <h2 className="text-3xl font-bold text-ocean-800 mb-8 text-center">Current Job Openings</h2>

          {jobsLoading && (
            <div className="text-center py-12 text-gray-500">Loading openings…</div>
          )}
          {jobsError && !jobsLoading && (
            <div className="text-center py-12 text-red-600">{jobsError}</div>
          )}
          {!jobsLoading && !jobsError && jobCategories.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No open positions right now — you can still send your CV using the application form below.
            </div>
          )}

          <div className="space-y-8">
            {jobCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-ocean-600 text-white px-6 py-3">
                  <h3 className="text-xl font-semibold">{category.category}</h3>
                </div>
                <div className="p-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {category.jobs.map((job) => (
                      <div key={job.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-ocean-300 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-ocean-800 text-lg mb-2">{job.title}</h4>
                          {job.isUrgent && (
                            <span className="shrink-0 bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded">Urgent</span>
                          )}
                        </div>
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            {job.location}
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                            {[EXPERIENCE_LABELS[job.experienceLevel] || job.experienceLevel, EMPLOYMENT_LABELS[job.employmentType] || job.employmentType].filter(Boolean).join(' · ')}
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                            {job.applicationDeadline
                              ? `Apply by: ${new Date(job.applicationDeadline).toLocaleDateString()}`
                              : `Posted: ${job.postedAt ? new Date(job.postedAt).toLocaleDateString() : '-'}`}
                          </div>
                          {decodeSalary(job.salaryRange) !== '' && (
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-2 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                              </svg>
                              {decodeSalary(job.salaryRange)}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleJobApply(job, category.category)}
                          className="mt-3 w-full bg-ocean-500 hover:bg-ocean-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          Apply Now
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Job Details Full Screen */}
        {showJobDetails && selectedJob && (
          <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-blue-100 to-ocean-100 z-50 overflow-y-auto">
            <div className="min-h-screen py-8 px-4">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-lg shadow-xl p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-3xl font-bold text-ocean-800 mb-2">{selectedJob.title}</h2>
                      <p className="text-lg text-ocean-600">{selectedJob.category}</p>
                    </div>
                    <button
                      onClick={() => setShowJobDetails(false)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 mr-3 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>Location:</strong> {selectedJob.location}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 mr-3 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      <span><strong>Experience:</strong> {selectedJob.experience}</span>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-ocean-800 mb-3">Job Description</h3>
                    <p className="text-gray-700 leading-relaxed">{selectedJob.description}</p>
                  </div>

                  {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-ocean-800 mb-3">Key Responsibilities</h3>
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {selectedJob.responsibilities.map((resp, index) => (
                          <li key={index}>{resp}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-ocean-800 mb-3">Requirements</h3>
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {selectedJob.requirements.map((req, index) => (
                          <li key={index}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="bg-ocean-50 border-l-4 border-ocean-500 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-ocean-800 mb-3">Ready to Apply?</h3>
                    <p className="text-gray-700 mb-4">
                      Click the button below to submit your application for this position.
                    </p>
                    <button
                      onClick={handleApplyFromDetails}
                      className="btn-primary px-8 py-3"
                    >
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Career Application Toggle Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowCareerForm(!showCareerForm)}
            className="btn-primary px-8 py-4 text-lg flex items-center mx-auto"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {showCareerForm ? 'Hide Seafarer Application Form' : 'Show Seafarer Application Form'}
          </button>
        </div>

        {/* Seafarer Application Form */}
        {showCareerForm && (
        <div id="career-application-form" className="maritime-card p-8 mb-12">
          <div className="flex items-center justify-center mb-8">
            <img src="/logo.jpg" alt="PetroMarine Logo" className="h-16 w-auto mr-4" />
            <div className="text-center">
              <h2 className="text-2xl font-bold text-ocean-800">Seafarer Application</h2>
              <p className="text-gray-600">PetroMarine Offshore Projects Support Co.</p>
              {selectedJob && (
                <p className="text-ocean-600 font-medium mt-2">
                  Applying for: {selectedJob.title} - {selectedJob.category}
                </p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* CV Upload Section */}
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-xl font-semibold text-ocean-800 mb-6">CV Upload / تحميل السيرة الذاتية</h3>
              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Upload your CV (PDF, DOC, DOCX) / تحميل السيرة الذاتية <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleCvUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-ocean-50 file:text-ocean-700 hover:file:bg-ocean-100 cursor-pointer border border-gray-300 rounded-lg p-2"
                    required
                  />
                  {cvFile && (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Selected: {cvFile.name}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-xl font-semibold text-ocean-800 mb-6">Personal Information</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name / الاسم <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email / البريد الإلكتروني <span className="text-gray-400 text-xs">(optional / اختياري)</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nationality / الجنسية <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nationality}
                    onChange={(e) => handleInputChange('nationality', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender / الجنس
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marital Status / الحالة الاجتماعية
                  </label>
                  <select
                    value={formData.maritalStatus}
                    onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth / تاريخ الميلاد <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile 1 / الهاتف الأول <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.mobile1}
                    onChange={(e) => handleInputChange('mobile1', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile 2 / الهاتف الثاني
                  </label>
                  <input
                    type="tel"
                    value={formData.mobile2}
                    onChange={(e) => handleInputChange('mobile2', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Relative Mobile / هاتف الأقارب
                  </label>
                  <input
                    type="tel"
                    value={formData.relativeMobile}
                    onChange={(e) => handleInputChange('relativeMobile', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available from / متاح من تاريخ
                  </label>
                  <input
                    type="date"
                    value={formData.availableFrom}
                    onChange={(e) => handleInputChange('availableFrom', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Applied to rank / الوظيفة المقدم لها <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.appliedToRank}
                    onChange={(e) => handleInputChange('appliedToRank', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="e.g., Captain, Chief Engineer, AB Seaman"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Home Address / عنوان المنزل
                </label>
                <textarea
                  value={formData.homeAddress}
                  onChange={(e) => handleInputChange('homeAddress', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Seaman Book Section */}
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-xl font-semibold text-ocean-800 mb-6">Marine Passport / الجواز البحري</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marine Passport No. / رقم الجواز البحري
                  </label>
                  <input
                    type="text"
                    value={formData.seamanBookNo}
                    onChange={(e) => handleInputChange('seamanBookNo', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profession / المهنة بالجواز البحري
                  </label>
                  <input
                    type="text"
                    value={formData.profession}
                    onChange={(e) => handleInputChange('profession', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Issue / تاريخ الإصدار
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfIssue}
                    onChange={(e) => handleInputChange('dateOfIssue', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date / تاريخ الانتهاء
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Certificates Section */}
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-xl font-semibold text-ocean-800 mb-6">Certificates / الشهادات</h3>
              <div className="space-y-4">
                {[
                  { key: 'securityAwareness', label: 'Security Awareness / التوعية الأمنية' },
                  { key: 'firstAid', label: 'First Aid / Medical Studies / اسعافات الطوارئ' },
                  { key: 'fireFighting', label: 'Fire Fighting / مكافحة الحرائق' },
                  { key: 'personalSurvival', label: 'Personal Sea Survival Techniques / تقنيات البقاء والإنقاذ والنجاة' },
                  { key: 'personalSafety', label: 'Personal Safety and Social Responsibilities / السلامة الشخصية والمسؤولية الاجتماعية' },
                  { key: 'medicalCertificate', label: 'International Medical Certificate for Seafarers / الشهادة الطبية البحرية' },
                  { key: 'cocOfficers', label: 'COC (Officers only) / شهادة الكفاءة' },
                  { key: 'gocDeck', label: 'GOC (Deck Officers only) / شهادة الخبرة بحرس وحفظ واستبصاف' },
                  { key: 'deckEngineWatch', label: 'Deck/Engine Watch Keeping Certificate (For Bosun, AB & Mechanics) / شهادة الخبرة بحرس ومتابعة واستبصاف' },
                  { key: 'shipSecurity', label: 'Ship Security Officer & Rescue Boats (For Bosun, AB & Mechanics) / ضابط أمن السفن وقوارب الإنقاذ' },
                  { key: 'foodHandler', label: 'Food Handler Certificate (For Cook) / شهادات تناول الطعام / الطباخ' }
                ].map((cert) => (
                  <div key={cert.key} className="grid md:grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg">
                    <div className="md:col-span-1">
                      <label className="text-sm font-medium text-gray-700">{cert.label}</label>
                    </div>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`${cert.key}_available`}
                          checked={(formData as any)[cert.key]?.available === true}
                          onChange={() => handleCertificateChange(cert.key, 'available', true)}
                          className="mr-2"
                        />
                        Yes / نعم
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`${cert.key}_available`}
                          checked={(formData as any)[cert.key]?.available === false}
                          onChange={() => handleCertificateChange(cert.key, 'available', false)}
                          className="mr-2"
                        />
                        No / لا
                      </label>
                    </div>
                    <div>
                      <input
                        type="date"
                        placeholder="Expiry Date / تاريخ الانتهاء"
                        value={(formData as any)[cert.key]?.expiry || ''}
                        onChange={(e) => handleCertificateChange(cert.key, 'expiry', e.target.value)}
                        disabled={!(formData as any)[cert.key]?.available}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sea Service Experience Section */}
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-ocean-800">Sea Service Experience / الخبرات البحرية</h3>
                <button
                  type="button"
                  onClick={addSeaServiceRow}
                  className="bg-ocean-500 hover:bg-ocean-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Row / إضافة صف
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-ocean-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Company Name / اسم الشركة</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Vessel Name / اسم السفينة</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Vessel Type / نوع السفينة</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Rank / الرتبة</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">From / من</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">To / إلى</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Experience (Years) / الخبرة (سنوات)</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Action / إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.seaService.map((service, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={service.companyName}
                            onChange={(e) => handleSeaServiceChange(index, 'companyName', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={service.vesselName}
                            onChange={(e) => handleSeaServiceChange(index, 'vesselName', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={service.vesselType}
                            onChange={(e) => handleSeaServiceChange(index, 'vesselType', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={service.rank}
                            onChange={(e) => handleSeaServiceChange(index, 'rank', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="date"
                            value={service.from}
                            onChange={(e) => handleSeaServiceChange(index, 'from', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="date"
                            value={service.to}
                            onChange={(e) => handleSeaServiceChange(index, 'to', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <div className="text-center font-medium text-ocean-600">
                            {service.experienceYears > 0 ? `${service.experienceYears} years` : '-'}
                          </div>
                        </td>
                        <td className="border border-gray-300 p-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeSeaServiceRow(index)}
                            disabled={formData.seaService.length <= 1}
                            className="bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                            title="Delete row / حذف الصف"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center pt-8">
              {seafarerError && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 max-w-xl mx-auto">
                  {seafarerError}
                </div>
              )}
              {seafarerSubmitted && (
                <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 max-w-xl mx-auto">
                  Application received — our HR team will review it and contact you. / تم استلام طلبك بنجاح
                </div>
              )}
              <button
                type="submit"
                disabled={seafarerSubmitting}
                className="btn-primary px-12 py-4 text-lg disabled:opacity-60"
              >
                {seafarerSubmitting ? 'Submitting… / جاري الإرسال' : 'Submit Application / تقديم الطلب'}
              </button>
            </div>
          </form>
        </div>
        )}
        
        <div className="maritime-card p-8 mb-12 text-center">
          <h2 className="text-2xl font-bold text-ocean-800 mb-4">Join Our Team</h2>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed mb-6">
            PetroMarine offers exciting career opportunities for maritime professionals. 
            We are always looking for qualified seafarers who share our commitment to safety, 
            quality, and environmental protection.
          </p>
          <div className="bg-ocean-50 border-l-4 border-ocean-500 p-4 max-w-3xl mx-auto">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-ocean-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-ocean-700 font-medium">
                  <strong>Application Required:</strong> All candidates must complete the seafarer application form below to be considered for employment.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          <div className="maritime-card p-6 text-center">
            <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-ocean-800 mb-2">Competitive Benefits</h3>
            <p className="text-gray-600 text-sm">Attractive salary packages and comprehensive benefits</p>
          </div>

          <div className="maritime-card p-6 text-center">
            <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-ocean-800 mb-2">Career Growth</h3>
            <p className="text-gray-600 text-sm">Professional development and advancement opportunities</p>
          </div>

          <div className="maritime-card p-6 text-center">
            <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-ocean-800 mb-2">Modern Fleet</h3>
            <p className="text-gray-600 text-sm">Work with state-of-the-art vessels and equipment</p>
          </div>
        </div>

        {/* Job Application Modal */}
        {showJobApplicationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-ocean-600 text-white p-4 rounded-t-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold">Apply for Position</h3>
                    <p className="text-sm opacity-90">{selectedJob?.title} - {selectedJob?.category}</p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {submittedRef ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-2">Application received</h4>
                  <p className="text-gray-600 text-sm mb-1">
                    Your application for <strong>{selectedJob?.title}</strong> was submitted successfully.
                  </p>
                  <p className="text-gray-500 text-xs mb-6">Reference: {submittedRef}</p>
                  <button
                    type="button"
                    onClick={() => { setShowJobApplicationModal(false); setShowJobDetails(false); setSubmittedRef(''); }}
                    className="px-6 py-2 bg-ocean-500 hover:bg-ocean-600 text-white rounded-lg transition-colors duration-200"
                  >
                    Done
                  </button>
                </div>
              ) : (
              <form onSubmit={handleJobApplicationSubmit} className="p-6 space-y-4">
                {/* CV Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CV File <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleJobCvUpload}
                    required
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-ocean-50 file:text-ocean-700 hover:file:bg-ocean-100 cursor-pointer border border-gray-300 rounded-lg p-2"
                  />
                  {jobApplicationData.cvFile && (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Selected: {jobApplicationData.cvFile.name}
                    </div>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={jobApplicationData.name}
                    onChange={(e) => handleJobApplicationInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="email"
                    value={jobApplicationData.email}
                    onChange={(e) => handleJobApplicationInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Enter your email address (optional)"
                  />
                </div>

                {/* Nationality */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nationality <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={jobApplicationData.nationality}
                    onChange={(e) => handleJobApplicationInputChange('nationality', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Enter your nationality"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={jobApplicationData.phone}
                    onChange={(e) => handleJobApplicationInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    value={jobApplicationData.address}
                    onChange={(e) => handleJobApplicationInputChange('address', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Enter your address"
                  />
                </div>

                {/* Birth Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={jobApplicationData.birthDate}
                    onChange={(e) => handleJobApplicationInputChange('birthDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                {/* Experience Years */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Years <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="50"
                    value={jobApplicationData.experienceYears}
                    onChange={(e) => handleJobApplicationInputChange('experienceYears', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Years of experience"
                  />
                </div>

                {/* Expected Salary */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Salary (USD) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={jobApplicationData.expectedSalary}
                    onChange={(e) => handleJobApplicationInputChange('expectedSalary', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Expected monthly salary"
                  />
                </div>

                {submitError && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {submitError}
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-ocean-500 hover:bg-ocean-600 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200"
                  >
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CareersPage;